import { Button, TextField, Grid, Autocomplete } from "@mui/material";
import { useEffect, useState } from "react";
import styles from "../components.module.sass";
import {
  ClientProps,
  DocumentProps,
  WellProps,
} from "../../interfaces/interfaces";
import { TitleComponent } from "../ui/TitleComponent";
import {
  additional_info_chem_options,
  additional_info_options,
  additional_info_prop_options,
} from "../../utils/data";
import { DatePicker } from "@mui/x-date-pickers";

interface Props {
  clientOptions: ClientProps[];
  wellOptions: WellProps[];
  type:
    | "pulling"
    | "chemical-tracker"
    | "proposal"
    | "tally"
    | "additional-document";
  fetchDataWells: (idWell: number) => void;
  onCancel: () => void;
  onUploadFile: (values: DocumentProps) => void;
  initialValues?: Partial<DocumentProps>;
  lockClientWell?: boolean;
}

const initial_form_state = {
  client: { id: 0, name: "" },
  well: {
    active: true,
    clientId: 0,
    contact: "",
    createdAt: "",
    id: 0,
    name: "",
    phoneNumber: "",
    updatedAt: "",
  },
  installationDate: null,
  customName: "",
  file: null,
  proposalId: null,
  tallyId: null,
  pullingDesignId: null,
};

export const UploadFileForm = ({
  clientOptions,
  wellOptions,
  type,
  fetchDataWells,
  onCancel,
  onUploadFile,
  initialValues,
  lockClientWell = false,
}: Props) => {
  const [values, setValues] = useState<DocumentProps>(initial_form_state);
  const [toSave, setToSave] = useState(false);
  const requiresWell = type !== "additional-document";

  useEffect(() => {
    if (!initialValues) return;
    setValues((prevState) => ({
      ...prevState,
      ...initialValues,
      file: prevState.file,
    }));
  }, [initialValues]);

  useEffect(() => {
    setToSave(
      Boolean(
        values.client?.id &&
          (!requiresWell || values.well?.id) &&
          (type !== "additional-document" || values.customName) &&
          values.file,
      ),
    );
  }, [requiresWell, values]);

  const onChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const pdfFile = event.target.files[0] || null;
      setValues((prevState) => {
        return {
          ...prevState,
          file: pdfFile,
        };
      });
    }
  };

  const onChangeClient = (clientSelected: ClientProps) => {
    fetchDataWells(clientSelected.id);
    setValues((prevState) => {
      return {
        ...prevState,
        client: clientSelected,
      };
    });
  };

  const onChangeWell = (wellSelected: WellProps) => {
    setValues((prevState) => {
      return {
        ...prevState,
        well: wellSelected,
      };
    });
  };

  const onChangeAdditionalInfo = (newValue: string) => {
    setValues((prevState) => {
      return {
        ...prevState,
        customName: newValue,
      };
    });
  };

  const defaultProps = {
    options: clientOptions,
    getOptionLabel: (option: ClientProps) => option.name,
  };

  const defaultPropsWellSelector = {
    options: wellOptions,
    getOptionLabel: (option: WellProps) => option.name,
  };

  const defaultPropsAdditionalInfo = () => {
    const typeOptionsMap = {
      pulling: {
        options: additional_info_options,
        getOptionLabel: (option: string) => option,
      },
      "chemical-tracker": {
        options: additional_info_chem_options,
        getOptionLabel: (option: string) => option,
      },
      tally: {
        options: additional_info_prop_options,
        getOptionLabel: (option: string) => option,
      },
      proposal: {
        options: additional_info_prop_options,
        getOptionLabel: (option: string) => option,
      },
      "additional-document": {
        options: [],
        getOptionLabel: (option: string) => option,
      },
    };
    return typeOptionsMap[type];
  };

  const onChangeDate = (newDate: Date | null) => {
    setValues((prevState) => {
      return {
        ...prevState,
        installationDate: newDate,
      };
    });
  };
  return (
    <div className={styles.customToolForm}>
      <TitleComponent title="Document Information" />
      <Grid container spacing={2}>
        <Grid item lg={12} xs={12}>
          <Autocomplete
            id="client-selector"
            value={values.client}
            isOptionEqualToValue={(option, value) => {
              return true;
            }}
            onChange={(_, newValue) => newValue && onChangeClient(newValue)}
            disabled={lockClientWell}
            renderOption={(props, option) => {
              return (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              );
            }}
            {...defaultProps}
            disablePortal
            className={styles.formFieldModalCustom}
            renderInput={(params) => <TextField {...params} label="Client" />}
          />
        </Grid>
        {requiresWell && (
          <Grid item lg={12} xs={12}>
            <Autocomplete
              id="well-selector"
              value={values.well}
              isOptionEqualToValue={(option, value) => {
                return true;
              }}
              onChange={(_, newValue) => newValue && onChangeWell(newValue)}
              disabled={lockClientWell}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.id}>
                    {option.name}
                  </li>
                );
              }}
              {...defaultPropsWellSelector}
              disablePortal
              className={styles.formFieldModalCustom}
              renderInput={(params) => <TextField {...params} label="Well" />}
            />
          </Grid>
        )}

        <Grid item lg={12} xs={12}>
          <DatePicker
            label="Date"
            value={values.installationDate || null}
            onChange={(newValue) => onChangeDate(newValue)}
            slots={{
              textField: (params) => (
                <TextField
                  {...params}
                  className={styles.formFieldModalCustom}
                />
              ),
            }}
          />
        </Grid>
        <Grid item lg={12} xs={12}>
          <Autocomplete
            freeSolo={type === "additional-document"}
            renderOption={(props, option) => {
              return (
                <li {...props} key={option}>
                  {option}
                </li>
              );
            }}
            value={values.customName}
            isOptionEqualToValue={(option, value) => {
              return true;
            }}
            onChange={(_, newValue) =>
              newValue && onChangeAdditionalInfo(newValue)
            }
            onInputChange={(_, newValue) =>
              type === "additional-document" && onChangeAdditionalInfo(newValue)
            }
            {...defaultPropsAdditionalInfo()}
            disablePortal
            className={styles.formFieldModalCustom}
            renderInput={(params) => (
              <TextField {...params} label="Additional Info" />
            )}
          />
        </Grid>

        <Grid item lg={12} xs={12}>
          <input
            id="outlined-basic"
            type="file"
            name="file"
            accept={
              type === "additional-document"
                ? ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                : undefined
            }
            className={styles.formFieldModalCustom}
            onChange={onChangeValue}
          />
        </Grid>
      </Grid>
      <div className={styles.buttonGral}>
        <Button onClick={onCancel} variant="contained">
          Cancel
        </Button>
        <Button
          onClick={() => onUploadFile(values)}
          variant="contained"
          disabled={!toSave}
        >
          Upload
        </Button>
      </div>
    </div>
  );
};
